import { newCorrelationId } from '@questaurant/lib-shared';
import { readConfig } from '../config/config';
import { ApiClientError, isRetryable } from './errors';

/**
 * The HTTP client (frontend design §6.2). A thin `fetch` wrapper — no axios —
 * responsible for base URL, correlation ID, timeout, error normalisation and
 * the GET retry policy. Domain modules (`api/health.ts`, …) build on it; they
 * never call `fetch` themselves.
 *
 * Auth-header injection and the refresh-token interceptor arrive in iteration 04
 * with the auth store; the seam for them is `buildHeaders`.
 */

const DEFAULT_TIMEOUT_MS = 10_000;
/** GETs retry twice on network/5xx with this backoff (§6.2). */
const RETRY_DELAYS_MS = [250, 750];

export interface RequestOptions {
  readonly signal?: AbortSignal;
  readonly timeoutMs?: number;
  readonly headers?: Record<string, string>;
}

function buildHeaders(extra: Record<string, string> = {}): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'X-Correlation-Id': newCorrelationId(),
    ...extra,
  };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Turn any failure into an `ApiClientError`. A non-JSON body (an HTML error
 * page from a proxy, say) must not surface as a JSON parse crash.
 */
async function toApiError(response: Response): Promise<ApiClientError> {
  let code = 'INTERNAL_ERROR';
  let message = `Request failed with status ${response.status}`;
  let details: ApiClientError['details'];
  let requestId: string | undefined;

  try {
    const body = (await response.json()) as {
      error?: {
        code?: string;
        message?: string;
        details?: ApiClientError['details'];
        requestId?: string;
      };
    };
    if (body?.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details;
      requestId = body.error.requestId;
    }
  } catch {
    // Keep the status-derived defaults.
  }

  return new ApiClientError({
    code,
    message,
    status: response.status,
    details,
    requestId,
  });
}

async function requestOnce<T>(
  method: string,
  path: string,
  options: RequestOptions,
  body?: unknown,
): Promise<T> {
  const { apiBaseUrl } = readConfig();
  const controller = new AbortController();
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  // Honour a caller-supplied signal alongside our timeout.
  if (options.signal) {
    if (options.signal.aborted) {
      controller.abort();
    } else {
      options.signal.addEventListener('abort', () => controller.abort(), {
        once: true,
      });
    }
  }

  try {
    const response = await fetch(`${apiBaseUrl}${path}`, {
      method,
      headers: buildHeaders(options.headers),
      credentials: 'include',
      signal: controller.signal,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });

    if (!response.ok) {
      throw await toApiError(response);
    }
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw error;
    }
    // Transport failure or timeout — status 0 marks "never reached the server".
    const aborted =
      error instanceof DOMException && error.name === 'AbortError';
    throw new ApiClientError({
      code: aborted ? 'TIMEOUT' : 'NETWORK_ERROR',
      message: aborted
        ? `Request timed out after ${timeoutMs}ms`
        : 'Could not reach the Questaurant API',
      status: 0,
    });
  } finally {
    clearTimeout(timer);
  }
}

export async function get<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      return await requestOnce<T>('GET', path, options);
    } catch (error) {
      lastError = error;
      const nextDelay = RETRY_DELAYS_MS[attempt];
      if (!isRetryable(error) || nextDelay === undefined) {
        throw error;
      }
      await delay(nextDelay);
    }
  }
  throw lastError;
}

/** Mutations are never auto-retried — they are not guaranteed idempotent (§6.2). */
export function post<T>(
  path: string,
  body: unknown,
  options: RequestOptions = {},
): Promise<T> {
  return requestOnce<T>('POST', path, options, body);
}
